import checkService from "../../services/checkService";
import leadService from "../../services/leadService";
import router from "../../router";
import { i18n } from "../../main.js";
import geoService from "../../services/geoService";
import { applyConditionalDiscounts, hasConditionalDiscount } from "../../helpers/conditionalDiscounts";


const getDefaultState = () => {
  return {
    competitorElecProduct: null,
    competitorGasProduct: null,
    megaElecProduct: null,
    megaGasProduct: null,
    elecProducts: null,
    gasProducts: null,
    myElecProduct: null,
    myGasProduct: null,
    peers: null,
    economyText: null,
    savingsElec: null,
    savingsGas: null,
    printLoading: false
  };
};

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    setCompetitorElecProduct(state, value) {
      state.competitorElecProduct = value;
    },
    setCompetitorGasProduct(state, value) {
      state.competitorGasProduct = value;
    },
    setMegaElecProduct(state, value) {
      state.megaElecProduct = value;
    },
    setMegaGasProduct(state, value) {
      state.megaGasProduct = value;
    },
    setElecProducts(state, value) {
      state.elecProducts = value;
    },
    setGasProducts(state, value) {
      state.gasProducts = value;
    },
    setMyElecProduct(state, value) {
      state.myElecProduct = value;
    },
    setMyGasProduct(state, value) {
      state.myGasProduct = value;
    },
    setEconomyText(state, value) {
      state.economyText = value;
    },
    setPeers(state, value) {
      state.peers = value;
    },
    setSavingsElec(state, value) {
      state.savingsElec = value;
    },
    setSavingsGas(state, value) {
      state.savingsGas = value;
    },
    reset(state) {
      Object.assign(state, getDefaultState());
    }
  },
  actions: {
    sendMail(context) {
      let data = {
        uuid: context.rootState.common.uuid,
        lang: context.rootState.common.lang,
        view: 'comparison'
      };

      leadService.getMail(data);
    },
    async generatePdf(context) {
      let data = {
        uuid: context.rootState.common.uuid,
        lang: context.rootState.common.lang,
        customerType: context.rootState.contact.isPro ? 'pro' : 'resi',
        view: 'comparison'
      };

      try {
        context.commit("common/setPrintLoading", true, { root: true });
        await leadService.getPdf(data);
        context.commit("common/setPrintLoading", false, { root: true });
      } catch (error) {
        context.commit("common/setPrintLoading", false, { root: true });
        throw error;
      }
    },
    async getComparison(context) {
      // await context.dispatch("resetComparison");
      let comparisonData = await context.dispatch("getComparisonData");
      try {
        let response = await checkService.getComparison(comparisonData);

        // ER-2932: the advertised prices must not include the conditional discounts.
        // Only the Mega products carry them, the competitor prices come from their own cards.
        [...((response.electricity || {}).mega || []), ...((response.gas || {}).mega || [])]
          .forEach(applyConditionalDiscounts);

        if (response.electricity) {
          let defaultCompElecProduct = response.electricity.competitor.find(
            product => product.byDefault === true
          );

          if (!context.state.myElecProduct) {
            let myElecProduct = ("structuredClone" in globalThis) ? structuredClone(defaultCompElecProduct) : Object.assign({}, defaultCompElecProduct);
            myElecProduct.id = "myElec";
            myElecProduct.peer = "myGas";
            myElecProduct.labelFr = "J'encode mes prix";
            myElecProduct.labelNl = "Ik vul m'n prijzen in";
            response.electricity.competitor.push(myElecProduct);
          } else {
            response.electricity.competitor.push(context.state.myElecProduct);
          }

          context.commit("setElecProducts", response.electricity);
          if (
            context.state.competitorElecProduct &&
            response.electricity.competitor.find(
              product => product.id === context.state.competitorElecProduct.id
            )
          ) {
            context.commit(
              "setCompetitorElecProduct",
              response.electricity.competitor.find(
                product => product.id === context.state.competitorElecProduct.id
              )
            );
          } else {
            context.commit("setCompetitorElecProduct", defaultCompElecProduct);
          }
        }

        if (response.gas) {
          let defaultCompGasProduct = response.gas.competitor.find(
            product => product.byDefault === true
          );
          if (!context.state.myGasProduct) {
            let myGasProduct = ("structuredClone" in globalThis) ? structuredClone(defaultCompGasProduct) : Object.assign({}, defaultCompGasProduct);
            myGasProduct.id = "myGas";
            myGasProduct.peer = "myElec";
            myGasProduct.labelFr = "J'encode mes prix";
            myGasProduct.labelNl = "Ik vul m'n prijzen in";
            response.gas.competitor.push(myGasProduct);
          } else {
            response.gas.competitor.push(context.state.myGasProduct);
          }
          context.commit("setGasProducts", response.gas);

          if (
            context.state.competitorGasProduct &&
            response.gas.competitor.find(
              product => product.id === context.state.competitorGasProduct.id
            )
          ) {
            context.commit(
              "setCompetitorGasProduct",
              response.gas.competitor.find(
                product => product.id === context.state.competitorGasProduct.id
              )
            );
          } else {
            context.commit("setCompetitorGasProduct", defaultCompGasProduct);
          }
        }

        context.commit("setPeers", response.peers);
        context.dispatch("setDefaultMegaProduct");
      } catch (error) {
        throw error;
      }
    },

    getComparisonData(context) {
      let consumptionsArray = context.rootState.consumption.consumptions.filter(
        consumption =>
          context.rootState.consumption.meterType.includes(
            consumption.meterType
          ) && consumption.meterType !== "gas"
      );

      let consumptions = consumptionsArray.reduce((acc, it) => {
        if (!(it.meterType.includes("injection"))) {
          if (it.meterType !== "mono") {
            acc[it.meterType] = it.value;
          } else {
            acc["day"] = it.value;
          }
        }
        return acc;
      }, {});

      let injections = consumptionsArray.reduce((acc, it) => {
        if (it.meterType.includes("injection")) {
          if (it.meterType == "injectionDay") {
            acc["injectionDay"] = it.value;
          }
          if (it.meterType == "injectionNight") {
            acc["injectionNight"] = it.value;
          }
          if (it.meterType == "injectionBase") {
            acc["injectionDay"] = it.value;
          }
        }
        return acc;
      }, {});

      let isPro = context.rootState.contact.isPro;

      let comparisonData = {
        customerType: isPro ? "pro" : "resi",
        promo: true,
        eco: true,
        postalCode: context.rootState.calculation.zipCode,
        town: context.rootState.calculation.city,
        area: context.rootState.calculation.area,
        instalment: true
      };

      // if(router.currentRoute.query["promo"]) comparisonData['promo'] = router.currentRoute.query["promo"];

      if (context.rootGetters["energy/hasElec"]) {
        let providerElec = context.rootState.calculation.providerElec;

        comparisonData.electricity = {
          provider: providerElec,
          powerPv: context.rootState.consumption.hasSolarPanels
            ? context.rootState.consumption.solarPanelsPower
            : null,
          consumption: consumptions,
          injection: injections,
          products: context.rootState.podium.simulationProducts.electricity,
          scenarioPv: context.rootState.consumption.hasSolarPanels
            ? context.rootState.consumption.solarPanelsScenario
            : null
        };

        // ER-2286: TI products (incentive tariff) require has_smart_meter / has_quarter_regime
        // / peak_level on the comparison payload regardless of region.
        const incentiveContext = context.rootGetters['common/incentiveContext'];
        if (geoService.getRegion(comparisonData.postalCode) === "BE-VLG" || incentiveContext) {
          comparisonData.hasSmartMeter = context.rootState.consumption.hasSmartMeter;

          if (comparisonData.hasSmartMeter) {
            comparisonData.hasQuarterRegime = context.rootState.consumption.hasQuarterRegime;
            comparisonData.peakLevel = context.rootState.consumption.peakLevel;
          }
        }

      }

      if (context.rootGetters["energy/hasGas"]) {
        let providerGas = context.rootState.calculation.providerGas;

        let consumptionGas = context.rootState.consumption.consumptions.find(
          consumption => consumption.meterType === "gas"
        ).value;

        comparisonData.gas = {
          provider: providerGas,
          consumption: consumptionGas,
          products: context.rootState.podium.simulationProducts.gas
        };
      }

      return comparisonData;
    },

    setCompetitorProduct(context, { energy: energy, product: product }) {
      if (energy === "elec") {
        context.commit("setCompetitorElecProduct", product);
      } else {
        context.commit("setCompetitorGasProduct", product);
      }

      context.dispatch("setDefaultMegaProduct");
    },

    setDefaultMegaProduct(context) {
      if (context.rootGetters["energy/hasElec"] && context.state.elecProducts) {
        let megaElecProducts = context.state.elecProducts.mega;
        let megaElecProduct = null;

        let peer = context.state.peers.find(
          peer =>
            peer.competitorProduct === context.state.competitorElecProduct.id
        );

        if (peer !== undefined) {
          megaElecProduct = megaElecProducts.find(
            product => product.id === peer.megaProduct
          );
        } else {
          if (context.state.megaElecProduct)
            megaElecProduct = context.state.megaElecProduct;
          else {
            megaElecProduct = megaElecProducts.reduce(
              (min, p) =>
                p.total.priceEuroVAT < min.total.priceEuroVAT ? p : min,
              megaElecProducts[0]
            );
          }
        }

        context.commit("setMegaElecProduct", megaElecProduct);
      }

      if (context.rootGetters["energy/hasGas"] && context.state.gasProducts) {
        let megaGasProducts = context.state.gasProducts.mega;
        let megaGasProduct = null;

        let peer = context.state.peers.find(
          peer =>
            peer.competitorProduct === context.state.competitorGasProduct.id
        );

        if (peer !== undefined) {
          megaGasProduct = context.state.gasProducts.mega.find(
            product => product.id === peer.megaProduct
          );
        } else {
          if (context.state.megaGasProduct)
            megaGasProduct = context.state.megaGasProduct;
          else {
            megaGasProduct = megaGasProducts.reduce(
              (min, p) =>
                p.total.priceEuroVAT < min.total.priceEuroVAT ? p : min,
              megaGasProducts[0]
            );
          }
        }

        context.commit("setMegaGasProduct", megaGasProduct);
      }
    },

    resetComparison(context) {
      context.commit("setElecProducts", null);
      context.commit("setGasProducts", null);
      context.commit("setPeers", null);
      context.commit("setMegaElecProduct", null);
      context.commit("setMegaGasProduct", null);
    },

    async save(context, event) {
      let data = {
        uuid: context.rootState.common.uuid
      };

      try {
        let comparison = {};
        comparison["electricity"] = context.state.elecProducts;
        comparison["gas"] = context.state.gasProducts;
        data["comparisonResult"] = comparison;
        data["simulation"] = 0

        data["savingsElec"] = context.state.savingsElec;
        data["savingsGas"] = context.state.savingsGas;

        if (context.state.megaElecProduct) {
          data["megaProductElec"] = context.state.megaElecProduct;
        }

        if (context.state.megaGasProduct) {
          data["megaProductGas"] = context.state.megaGasProduct;
        }

        if (context.state.competitorElecProduct) {
          let competitorElec = context.state.competitorElecProduct;

          if (competitorElec.id == "myElec") {
            let elecEnergyAmount = 0;
            let elecRenewableAmount = 0;

            Object.keys(competitorElec["energy"]).forEach(key => {
              let amount =
                parseFloat(competitorElec.energy[key].unitaryPriceEuroVAT) *
                  competitorElec.energy[key].quantity;

              elecEnergyAmount += amount;
              elecRenewableAmount +=
                parseFloat(
                  competitorElec.subtotal.renewable.unitaryPriceEuroVAT
                ) * competitorElec.energy[key].quantity;

              competitorElec.energy[key].priceEuroVAT = amount;
            })

            elecEnergyAmount += parseFloat(
              competitorElec.subscription.priceEuroVAT
            );

            let elecTaxes = competitorElec.subtotal.taxes.priceEuroVAT;
            let elecTransport = competitorElec.subtotal.transportAndDistribution.priceEuroVAT;
            let elecProsumer = competitorElec.subtotal.prosumer.priceEuroVAT;
            let elecForfaitPv = competitorElec.subtotal.forfaitPv !== undefined ? range.electricity.subtotal.forfaitPv.priceEuroVAT : null;

            competitorElec.subtotal.renewable.priceEuroVAT = elecRenewableAmount;
            competitorElec.total.priceEuroVAT = elecEnergyAmount + elecRenewableAmount + elecTaxes + elecTransport + elecProsumer + (elecForfaitPv? elecForfaitPv : 0);
            competitorElec.total.priceEuroMonthlyVAT = competitorElec.total.priceEuroVAT / 12;
          }

          data["competitorProductElec"] = competitorElec;
        }

        if (context.state.competitorGasProduct) {
          let competitorGas = context.state.competitorGasProduct;

          if (competitorGas.id == "myGas") {
            let gasEnergyAmount = parseFloat(competitorGas.energy.exclusiveDay.unitaryPriceEuroVAT) *
              competitorGas.energy.exclusiveDay.quantity;

            let gasSub = competitorGas.subscription.priceEuroVAT;
            let gasTaxes = competitorGas.subtotal.taxes.priceEuroVAT;
            let gasTransport = competitorGas.subtotal.transportAndDistribution.priceEuroVAT;

            competitorGas.energy.exclusiveDay.priceEuroVAT = gasEnergyAmount
            competitorGas.total.priceEuroVAT = gasEnergyAmount + gasSub + gasTaxes + gasTransport;
            competitorGas.total.priceEuroMonthlyVAT = competitorGas.total.priceEuroVAT / 12;
          }

          data["competitorProductGas"] = competitorGas;
        }

        if (context.rootState.calculation.providerElec) {
          data["providerElec"] = context.rootState.calculation.providerElec;
        }

        if (context.rootState.calculation.providerGas) {
          data["providerGas"] = context.rootState.calculation.providerGas;
        }

        await leadService.save(data);
      } catch (error) {
        throw error;
      }
    }
  },
  getters: {
    productName: state => product => {
      return `${product.descriptions[i18n.locale]}`;
    },
    findPeer: state => product => {
      let energyChanged = product.energyType;
      let peerProduct = null;

      if (energyChanged === "gas") {
        if (state.elecProducts) {
          let allElecProducts = state.elecProducts.mega.concat(
            state.elecProducts.competitor
          );
          peerProduct = allElecProducts.find(
            peerProduct => peerProduct.id === product.peer
          );
        }
      } else {
        if (state.gasProducts) {
          let allGasProducts = state.gasProducts.mega.concat(
            state.gasProducts.competitor
          );
          peerProduct = allGasProducts.find(peerProduct => {
            return peerProduct.id === product.peer;
          });
        }
      }

      return peerProduct;
    },
    hasMegaDiscount: state => {
      let hasAnyDiscount = product => !!product && (!!product.subtotal.discount || hasConditionalDiscount(product));

      return hasAnyDiscount(state.megaElecProduct) || hasAnyDiscount(state.megaGasProduct);
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/comparison.js